
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  
  const { user, resetPassword, isLoading } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is authenticated (auto-logged in via recovery link)
  useEffect(() => {
    if (!isLoading && !user) {
      toast.error("Invalid password reset session. Please request a new password reset.");
      navigate("/auth");
    }
  }, [user, navigate, isLoading]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error, success } = await resetPassword(password);
      if (error) {
        setError(error.message);
      } else if (success) {
        setIsSuccess(true);
        toast.success("Password updated successfully!");
        
        // Redirect to home page after a short delay
        setTimeout(() => {
          navigate("/");
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Don't render if user is not authenticated
  if (!user) {
    return null;
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <div className="relative pt-24 pb-16 flex-1 flex flex-col items-center justify-center px-6">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-0 h-[500px] w-[1000px] -translate-x-1/2 bg-gradient-to-br from-primary/20 via-blue-50 to-background blur-3xl"></div>
        </div>
        
        <div className="relative w-full max-w-md">
          <div className="bg-white shadow-xl rounded-2xl p-8">
            {!isSuccess ? (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-bold font-display">Set New Password</h2>
                  <p className="text-muted-foreground mt-2">
                    Enter your new password below.
                  </p>
                </div>
                
                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
                    {error}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="flex gap-1 items-center">
                      <Lock className="h-4 w-4" /> New Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="flex gap-1 items-center">
                      <Lock className="h-4 w-4" /> Confirm New Password
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-11 text-base"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Updating..." : "Update Password"}
                  </Button>
                </form>
              </div>
            ) : (
              <div className="space-y-6 text-center">
                <div className="flex justify-center">
                  <CheckCircle className="h-16 w-16 text-green-500" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold font-display text-green-700">Password Updated!</h2>
                  <p className="text-muted-foreground mt-2">
                    Your password has been successfully updated. You will be redirected to the homepage.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
